import { useEffect, useState } from 'react';

type FeedKey = 'general' | 'espoirs' | 'podcast';

interface FeedItem {
  title: string;
  link?: string;
  pubDate?: string;
  description?: string;
  // fullDescription keeps the un-truncated, decoded plain-text description
  fullDescription?: string;
  image?: string;
  // dynamic props like enclosureUrl may be present
  [k: string]: any;
}

const RSS_FEEDS: Record<FeedKey, string> = {
  general: 'https://www.toutsurlehockey.com/rss',
  espoirs: 'https://www.toutsurlehockey.com/tslh-espoirs/rss',
  podcast: 'https://anchor.fm/s/10913ea48/podcast/rss',
};

const RSS_FEED_FALLBACKS: Partial<Record<FeedKey, string[]>> = {
  general: [
    'https://www.toutsurlehockey.com/feed',
  ],
  espoirs: [
    'https://www.toutsurlehockey.com/category/espoirs/feed',
  ],
};

function getFeedCandidates(key: FeedKey): string[] {
  return [RSS_FEEDS[key], ...(RSS_FEED_FALLBACKS[key] || [])];
}

function isLocalDevHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

async function fetchFromServerProxy(key: FeedKey): Promise<string> {
  const response = await fetch(`/.netlify/functions/rss-proxy?key=${encodeURIComponent(key)}`);
  if (!response.ok) {
    throw new Error(`rss-proxy failed: ${response.status}`);
  }

  return await response.text();
}

async function fetchWithCorsFallback(url: string) {
  try {
    const res = await fetch(url);
    if (res.ok) return await res.text();
  } catch (e) {
    // fall through to proxy
  }
  const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
  const fallbackRes = await fetch(proxy);
  if (!fallbackRes.ok) throw new Error('RSS proxy failed');
  return await fallbackRes.text();
}

function normalizeText(value?: string): string | undefined {
  if (!value) return undefined;
  const tmp = document.createElement('div');
  tmp.innerHTML = value;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
}

function mapJsonFeedItems(items: any[]): FeedItem[] {
  if (!Array.isArray(items)) return [];

  return items.map((item) => {
    const rawTitle = item?.title || 'Sans titre';
    const title = normalizeText(rawTitle) || 'Sans titre';
    const link = item?.link || undefined;
    const pubDate = item?.pubDate || item?.pub_date || undefined;
    const rawDescription = item?.description || item?.content || item?.summary || undefined;
    const fullDescription = normalizeText(rawDescription);
    const description = fullDescription ? fullDescription.slice(0, 240) : undefined;
    const image = item?.thumbnail || item?.enclosure?.link || undefined;
    const enclosureUrl = item?.enclosure?.link || undefined;

    return { title, link, pubDate, description, fullDescription, image, enclosureUrl } as FeedItem;
  });
}

async function fetchViaRss2Json(url: string): Promise<FeedItem[]> {
  const endpoint = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}`;
  const res = await fetch(endpoint);
  if (!res.ok) throw new Error('rss2json failed');

  const json = await res.json();
  if (json?.status !== 'ok') throw new Error('rss2json invalid status');

  return mapJsonFeedItems(json.items || []);
}

async function loadFeedItems(key: FeedKey): Promise<FeedItem[]> {
  // Production: éviter les appels cross-origin côté navigateur pour supprimer les erreurs CORS.
  try {
    const xml = await fetchFromServerProxy(key);
    const parsed = parseRss(xml);
    if (parsed.length > 0) return parsed;
  } catch (e) {
    // Continue with local-dev fallback only
  }

  // Fallback de dev local uniquement
  if (isLocalDevHost()) {
    const candidates = getFeedCandidates(key);

    for (const url of candidates) {
      try {
        const txt = await fetchWithCorsFallback(url);
        const parsed = parseRss(txt);
        if (parsed.length > 0) return parsed;
      } catch (e) {
        // continue
      }
    }

    for (const url of candidates) {
      try {
        const parsed = await fetchViaRss2Json(url);
        if (parsed.length > 0) return parsed;
      } catch (e) {
        // continue
      }
    }
  }

  return [];
}

function parseRss(xmlText: string): FeedItem[] {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'application/xml');
    // Try to get a channel-level image (common for podcasts)
    const channelItunesImage = doc.getElementsByTagName('itunes:image')[0]?.getAttribute('href');
    const channelImageUrl = channelItunesImage || doc.querySelector('channel > image > url')?.textContent?.trim();
    const items = Array.from(doc.querySelectorAll('item'));
    return items.map((i) => {
      // Helper to decode HTML entities (e.g. &#8217; -> ’)
      const decodeHtmlEntities = (str?: string) => {
        if (!str) return str;
        // Create a dummy element to leverage browser's decoding
        const txt = document.createElement('textarea');
        txt.innerHTML = str;
        return txt.value;
      };

      const rawTitle = i.querySelector('title')?.textContent?.trim() || 'Sans titre';
      const title = decodeHtmlEntities(rawTitle) || 'Sans titre';
      const link = i.querySelector('link')?.textContent?.trim() || i.querySelector('guid')?.textContent?.trim() || undefined;
      const pubDate = i.querySelector('pubDate')?.textContent?.trim() || undefined;
      // Try several common fields for the textual description (description, content:encoded, summary)
      let description = i.querySelector('description')?.textContent?.trim()
        || (i.querySelector('content\:encoded') as Element | null)?.textContent?.trim()
        || i.querySelector('summary')?.textContent?.trim()
        || undefined;
      const enclosure = i.querySelector('enclosure') as Element | null;
      let image: string | undefined;
      let enclosureUrl: string | undefined;
      if (enclosure?.getAttribute('url')) {
        const url = enclosure.getAttribute('url') || undefined;
        if (url && /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url)) image = url;
        else enclosureUrl = url;
      }
      // 1) Check common RSS tags used by podcasts (itunes:image, media:thumbnail, media:content)
      if (!image) {
        const itunesImage = (i.getElementsByTagName('itunes:image')[0] as Element | undefined)?.getAttribute('href');
        const mediaThumb = (i.getElementsByTagName('media:thumbnail')[0] as Element | undefined)?.getAttribute('url');
        const mediaContent = (i.getElementsByTagName('media:content')[0] as Element | undefined)?.getAttribute('url');
        const candidate = itunesImage || mediaThumb || mediaContent;
        if (candidate && /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(candidate)) image = candidate;
      }
      // If still no image, try to extract an <img> from the description
      // Important: do NOT overwrite `description` here — we want to keep the original
      // HTML description so the later `fullDescription` decoding can access full content.
      if (!image && description) {
        const m = description.match(/<img[^>]+src=["']([^"']+)["']/i);
        if (m) image = m[1];
        // don't mutate `description` here; fullDescription handling below will strip tags and slice
      }

      // If the item has no artwork, fall back to the channel-level image (common for podcasts)
      if (!image && channelImageUrl) {
        image = channelImageUrl;
      }

          // Always strip HTML tags and decode entities from description so the UI shows plain text
          let fullDescription: string | undefined = undefined;
          if (description) {
            try {
              const tmp = document.createElement('div');
              tmp.innerHTML = description;
              // textContent will give decoded, plain text
              const decoded = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
              fullDescription = decoded;
              description = decoded.slice(0, 240);
            } catch (e) {
              // fallback to simple decode
              const decoded = decodeHtmlEntities(description)?.replace(/<[^>]+>/g, '').trim() || '';
              fullDescription = decoded;
              description = decoded.slice(0, 240);
            }
          }
      return { title, link, pubDate, description, fullDescription, image, enclosureUrl } as FeedItem;
    });
  } catch (e) {
    console.error('parseRss error', e);
    return [];
  }
}

/* --- Caching & prefetch utilities --- */
// TTL pour le cache (ms) — adapter si souhaité. 5 minutes = 5*60*1000
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes pour garder le pool réactif mais pas trop vieux
const CACHE_PREFIX = 'lnh.rss.';

type CachedFeed = { ts: number; items: FeedItem[] };

function getCacheKey(key: FeedKey) {
  return `${CACHE_PREFIX}${key}`;
}

function readCachedFeed(key: FeedKey): CachedFeed | null {
  try {
    const cacheKey = getCacheKey(key);
    const raw = sessionStorage.getItem(cacheKey) || localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedFeed;
    return parsed;
  } catch (e) {
    console.warn('readCachedFeed error', e);
    return null;
  }
}

function writeCachedFeed(key: FeedKey, items: FeedItem[]) {
  if (!items || items.length === 0) return;

  try {
    const payload: CachedFeed = { ts: Date.now(), items };
    const serialized = JSON.stringify(payload);
    const cacheKey = getCacheKey(key);
    sessionStorage.setItem(cacheKey, serialized);
    localStorage.setItem(cacheKey, serialized);
  } catch (e) {
    console.warn('writeCachedFeed error', e);
  }
}

async function prefetchFeed(key: FeedKey) {
  try {
    const parsed = await loadFeedItems(key);
    writeCachedFeed(key, parsed);
    return parsed;
  } catch (e) {
    console.debug(`prefetchFeed ${key} failed`, e);
    return null;
  }
}

export default function LnhNewsSection({ compact = true }:{ compact?: boolean }){
  const [active, setActive] = useState<FeedKey>('general');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<FeedItem[]>([]);

  // Orange palette matching the provided image
  const orangeGradient = 'linear-gradient(90deg,#ff7a00 0%,#ff9a33 50%,#ff7a00 100%)';
  const orangeColor = '#ff7a00';

  useEffect(() => {
    let mounted = true;
    async function load(key: FeedKey) {
      setLoading(true);

      // 1) Try immediate cached response (stale-if-needed)
      const cached = readCachedFeed(key);
      const freshEnough = cached && (Date.now() - cached.ts) < CACHE_TTL;

      if (mounted && cached && cached.items && cached.items.length > 0) {
        // show cached instantly
        setItems(cached.items.slice(0, compact ? 4 : 12));
        // if cached is stale, we'll revalidate below
      }

      // 2) If cache is fresh, avoid network; else fetch/refresh in background
      if (freshEnough) {
        setLoading(false);
        // still trigger a background refresh occasionally (best-effort) if older than half TTL
        if (Date.now() - (cached!.ts) > (CACHE_TTL / 2)) {
          prefetchFeed(key)
            .then((prefetched) => {
              if (!mounted || !prefetched) return;
              setItems(prefetched.slice(0, compact ? 4 : 12));
            })
            .catch(() => {});
        }
        return;
      }

      try {
        const parsed = await loadFeedItems(key);
        if (!mounted) return;
        // write cache and display
        writeCachedFeed(key, parsed);
        setItems(parsed.slice(0, compact ? 4 : 12));
      } catch (e) {
        // Keep this failure silent for users (network/CORS can be transient).
        if (!cached || !cached.items || cached.items.length === 0) {
          setItems([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load(active);

    return () => { mounted = false; };
  }, [active, compact]);

  return (
    <div className="lnh-news-section">
      <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between mb-3">
        <h6 className="mb-2 mb-sm-0">Actualités LNH</h6>
        <div className="btn-group btn-group-sm flex-wrap" role="group" style={{ gap: 6 }}>
          <button
            className={`btn ${active === 'general' ? '' : 'btn-outline-warning'}`}
            style={active === 'general' ? { background: orangeGradient, color: '#fff', borderColor: orangeColor } : undefined}
            onClick={() => setActive('general')}
          >Général</button>
          <button
            className={`btn ${active === 'espoirs' ? '' : 'btn-outline-warning'}`}
            style={active === 'espoirs' ? { background: orangeGradient, color: '#fff', borderColor: orangeColor } : undefined}
            onClick={() => setActive('espoirs')}
          >Espoirs</button>
          <button
            className={`btn ${active === 'podcast' ? '' : 'btn-outline-warning'}`}
            style={active === 'podcast' ? { background: orangeGradient, color: '#fff', borderColor: orangeColor } : undefined}
            onClick={() => setActive('podcast')}
          >Podcast</button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-3">
          <div className="spinner-border text-warning" role="status"><span className="visually-hidden">Chargement...</span></div>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-muted small">Aucun article disponible.</div>
      )}

      {!loading && items.length > 0 && (
        <div className="list-group">
          {items.map((it, i) => {
            // Determine if the description is actually truncated so we can show the "lire la suite" link.
            const descLen = it.description ? it.description.length : 0;
            const fullLen = it.fullDescription ? it.fullDescription.length : 0;
            // If we have a fullDescription, compare lengths. Otherwise, assume truncation when the shown description is at the slice limit (240).
            const isTruncated = (fullLen > descLen) || (descLen >= 240);

            return (
            <div key={`${active}-${i}`} className="list-group-item d-flex flex-column flex-sm-row gap-3 align-items-start">
              <a className="stretched-link" href={it.link || '#'} target="_blank" rel="noreferrer noopener" aria-hidden="true" />
              <div style={{ width: 96, height: 64, borderRadius: 6, overflow: 'hidden', position: 'relative' }} className="flex-shrink-0">
                {/* Fallback background / badge always present underneath the image. If the image fails to load we hide it and the fallback remains visible. */}
                <div style={{ width: '100%', height: '100%', background: orangeColor, borderRadius: 6, backgroundImage: 'linear-gradient(90deg, rgba(255,122,0,0.15), rgba(255,154,51,0.05))' }} className="d-flex align-items-center justify-content-center text-white fw-bold">TLSH</div>
                {it.image && (
                  <img
                    src={it.image}
                    alt=""
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }}
                  />
                )}
              </div>
              <div className="flex-grow-1 w-100">
                <div className="d-flex flex-column flex-sm-row justify-content-between">
                  <div className="mb-2 mb-sm-0">
                    <div className="fw-semibold">{it.title}</div>
                    {it.description && (
                      <div className="text-muted small">
                        {it.description}
                        {isTruncated && it.link && (
                          // Render a single ellipsis + link like "...lire la suite" which points to the article page
                          <>
                            {' '}
                            <a href={it.link} target="_blank" rel="noreferrer noopener">…lire la suite</a>
                          </>
                        )}
                      </div>
                    )}
                    {/* If podcast episode with enclosure, show player/link */}
                    {active === 'podcast' && (it as any).enclosureUrl && (
                      <div className="mt-2 d-sm-none">
                        <a className="btn btn-sm btn-outline-primary w-100" href={(it as any).enclosureUrl} target="_blank" rel="noreferrer noopener">Écouter l'épisode</a>
                      </div>
                    )}
                  </div>
                  <div className="text-end text-muted small ms-sm-3">
                    {it.pubDate ? new Date(it.pubDate).toLocaleString('fr-CA') : ''}
                    {active === 'podcast' && (it as any).enclosureUrl && (
                      <div className="d-none d-sm-block mt-2">
                        <a className="btn btn-sm btn-outline-primary" href={(it as any).enclosureUrl} target="_blank" rel="noreferrer noopener">Écouter</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}
